import { ɵɵdefineInjectable, Injectable, Component, ChangeDetectorRef, ElementRef, InjectionToken, Directive, Optional, Inject, Renderer2, ComponentFactoryResolver, Injector, Input, NgModule, Pipe } from '@angular/core';
import { combineLatest, Observable, Subscription, BehaviorSubject } from 'rxjs';
import { map, distinctUntilChanged, take, debounceTime } from 'rxjs/operators';

import * as ɵngcc0 from '@angular/core';
class LoadingToken {
    constructor(value = true, source = true, unique) {
        this.value = value;
        this.source = source;
        this.unique = unique;
    }
    isSame(a = true, unique) {
        if (a === this.source || a === this.value)
            return true;
        if (this.unique && unique && this.unique === unique)
            return true;
        return false;
    }
}
class IsLoadingService {
    constructor() {
        this.defaultKey = "default";
        // provides an observable indicating if a particular key is loading
        this.loadingSubjects = new Map();
        // tracks how many "things" are loading for each key
        this.loadingStacks = new Map();
        // tracks the sum of the loading indicators and subscribers for each
        // key so that unneeded keys can be deleted/garbage collected.
        this.loadingKeyIndex = new Map();
    }
    /**
     * Used to determine if something is loading or not.
     *
     * When called without arguments, returns the default *isLoading*
     * observable for your app. When called with an options object
     * containing a `key` property, returns the *isLoading* observable
     * corresponding to that key. When called with an array of keys,
     * returns an observable that emits true while any key is loading
     * and false otherwise.
     *
     * Internally, *isLoading* observables are `BehaviorSubject`s, so
     * they will return values immediately upon subscription.
     *
     * When called, this method creates a new observable and returns it.
     * This means that you should not use this method directly in an Angular
     * template because each time the method is called it will look
     * (to Angular change detection) like the value has changed. To make
     * subscribing in templates easier, check out the `IsLoadingPipe`.
     * Alternatively, store the observable returned from this method in
     * a variable on your component and use that variable inside your
     * template.
     *
     * Example:
     *
     * ```ts
     *  class MyCustomComponent implements OnInit {
     *    variableForUseInTemplate: Observable<boolean>;
     *
     *    constructor(private loadingService: IsLoadingService) {}
     *
     *    ngOnInit() {
     *      this.variableForUseInTemplate =
     *        this.loadingService.isLoading$({key: 'button'});
     *
     *      this.loadingService.isLoading$().subscribe(value => {
     *        // ... do stuff
     *      });
     *
     *      this.loadingService
     *        .isLoading$({key: MyCustomComponent})
     *        .subscribe(value => {
     *          // ... do stuff
     *        });
     *    }
     *  }
     * ```
     *
     * @param args.key optionally specify the key to subscribe to
     */
    isLoading$(args = {}) {
        if (Array.isArray(args.key)) {
            if (args.key.length === 0) {
                throw new Error(`Must provide at least one key when passing an array of keys`);
            }
            return combineLatest(args.key.map((key) => this.isLoading$({ key }))).pipe(map((values) => values.some((v) => v)), distinctUntilChanged());
        }
        const keys = this.normalizeKeys(args.key);
        return new Observable((observer) => {
            // this function will called each time this
            // Observable is subscribed to.
            this.indexKeys(keys);
            const subscription = this.loadingSubjects
                .get(keys[0])
                .pipe(distinctUntilChanged())
                .subscribe(observer);
            // the return value is the teardown function,
            // which will be invoked when the new
            // Observable is unsubscribed from.
            return () => {
                subscription.unsubscribe();
                keys.forEach((key) => this.deIndexKey(key));
            };
        });
    }
    /**
     * Same as `isLoading$()` except a boolean is returned,
     * rather than an observable.
     *
     * @param args.key optionally specify the key to check
     */
    isLoading(args = {}) {
        const keys = this.normalizeKeys(args.key);
        return keys
            .map((key) => { var _a, _b; return (_b = (_a = this.loadingSubjects.get(key)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : false; })
            .some((v) => v);
    }
    add(a, b) {
        const options = b ||
            (!(a instanceof Promise ||
                a instanceof Subscription ||
                a instanceof Observable) &&
                a) ||
            undefined;
        let subs;
        let source;
        const teardown = (s) => () => this.remove(s, options);
        const keys = this.normalizeKeys(options === null || options === void 0 ? void 0 : options.key);
        if (a instanceof Subscription || a instanceof Observable) {
            source = a;
            subs =
                a instanceof Observable
                    ? keys.map(() => a.pipe(take(1)).subscribe())
                    : keys.map(() => a);
            if (subs[0].closed)
                return a;
            subs.forEach((s) => s.add(teardown(s)));
        }
        else if (a instanceof Promise) {
            source = a;
            // important for `subs` to contain the original promise and not a new
            // promise created by `Promise#then()`. Hence the separate `forEach`
            // call below
            subs = keys.map(() => a);
            // Unfortunately, if the promise is already resolved,
            // this still executes asynchronously. There is no way around this.
            subs.forEach((s) => s.then(teardown(a), teardown(a)));
        }
        this.indexKeys(keys);
        keys.forEach((key, keyIndex) => {
            const sub = subs && subs[keyIndex];
            const loadingStack = this.loadingStacks.get(key);
            // if the "unique" option is present, remove any existing
            // loading idicators with the same "unique" value
            if (options === null || options === void 0 ? void 0 : options.unique) {
                const index = loadingStack.findIndex((t) => t.isSame(sub, options === null || options === void 0 ? void 0 : options.unique));
                if (index >= 0) {
                    const loadingToken = loadingStack.splice(index, 1)[0];
                    if (loadingToken.source instanceof Observable) {
                        loadingToken.value.unsubscribe();
                    }
                }
            }
            loadingStack.push(new LoadingToken(sub, a instanceof Observable ? a : sub, options === null || options === void 0 ? void 0 : options.unique));
            this.updateLoadingStatus(key);
        });
        return source;
    }
    remove(a, b) {
        let options = b;
        let sub;
        if (a instanceof Subscription ||
            a instanceof Promise ||
            a instanceof Observable) {
            sub = a;
        }
        else {
            options = a;
        }
        const keys = this.normalizeKeys(options === null || options === void 0 ? void 0 : options.key);
        for (const key of keys) {
            const loadingStack = this.loadingStacks.get(key);
            // !loadingStack means that a user has called remove() needlessly
            if (!loadingStack)
                continue;
            const index = loadingStack.findIndex((t) => t.isSame(sub));
            if (index < 0)
                continue;
            const loadingToken = loadingStack.splice(index, 1)[0];
            if (sub instanceof Observable && loadingToken.source === sub) {
                loadingToken.value.unsubscribe();
            }
            this.updateLoadingStatus(key);
            this.deIndexKey(key);
        }
    }
    // /**
    //  * Clears all the loading indicators for the given key or keys,
    //  * reseting the loading status for those keys to `false`.
    //  *
    //  * Use `"default"` to clear the default key.
    //  */
    // clear(key: Key | Key[]) {
    //   const keys = this.normalizeKeys(key);
    //   for (const k of keys) {
    //     const loadingStack = this.loadingStacks.get(k);
    //     // !loadingStack means that a user has called clear() needlessly
    //     if (!loadingStack) continue;
    //     for (const loadingToken of loadingStack) {
    //       this.deIndexKey(k);
    //       if (!loadingToken.isManagedByIsLoadingService) continue;
    //       (loadingToken.value as Subscription).unsubscribe();
    //     }
    //     this.loadingStacks.set(k, []);
    //     this.updateLoadingStatus(k);
    //   }
    // }
    normalizeKeys(key) {
        if (!key)
            key = [this.defaultKey];
        else if (!Array.isArray(key))
            key = [key];
        return key;
    }
    /**
     * `indexKeys()` along with `deIndexKeys()` helps us track which
     * keys are being watched so that unused keys can be deleted
     * / garbage collected.
     *
     * `indexKeys` is called both when there's a new subscriber to
     * a key as well as when a loading indicator is added for a key.
     * A key is only deIndexed when all of the subscribers have
     * unsubscribed *and* all of the loading indicators have been
     * removed.
     *
     * When `indexKeys()` is called with an array of keys,
     * we need to make sure that a loadingSubject
     * and loadingStack exists for that key. We also need to increase
     * the index for that key in the `loadingKeyIndex` map (which
     * tracks the sum of the subscribers and loading indicators for
     * that key).
     *
     * When `deIndexKeys()` is called with an array of keys, it
     * means that either a subscriber has unsubscribed from a key or
     * a loading stack counter has been removed for a key. For keys
     * where the index reaches 0, we need
     * to delete the associated `loadingKeyIndex`, `loadingSubject`,
     * and `loadingStack`. So that the `key` can be properly
     * released for garbage collection.
     */
    indexKeys(keys) {
        for (const key of keys) {
            if (this.loadingKeyIndex.has(key)) {
                const curr = this.loadingKeyIndex.get(key);
                this.loadingKeyIndex.set(key, curr + 1);
            }
            else {
                const subject = new BehaviorSubject(false);
                this.loadingKeyIndex.set(key, 1);
                this.loadingSubjects.set(key, subject);
                this.loadingStacks.set(key, []);
            }
        }
    }
    deIndexKey(key) {
        const curr = this.loadingKeyIndex.get(key);
        if (curr === 1) {
            this.loadingKeyIndex.delete(key);
            this.loadingSubjects.delete(key);
            this.loadingStacks.delete(key);
        }
        else {
            this.loadingKeyIndex.set(key, curr - 1);
        }
    }
    updateLoadingStatus(key) {
        const loadingStatus = this.loadingStacks.get(key).length > 0;
        this.loadingSubjects.get(key).next(loadingStatus);
    }
}
IsLoadingService.ɵfac = function IsLoadingService_Factory(t) { return new (t || IsLoadingService)(); };
IsLoadingService.ɵprov = ɵɵdefineInjectable({ factory: function IsLoadingService_Factory() { return new IsLoadingService(); }, token: IsLoadingService, providedIn: "root" });
IsLoadingService.ctorParameters = () => [];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IsLoadingService, [{
        type: Injectable,
        args: [{
                providedIn: "root"
            }]
    }], function () { return []; }, null); })();

class IsLoadingSpinnerComponent {
    // don't need change detection for this component
    constructor(cdr, el) {
        this.cdr = cdr;
        this.el = el;
        this.cdr.detach();
    }
}
IsLoadingSpinnerComponent.ɵfac = function IsLoadingSpinnerComponent_Factory(t) { return new (t || IsLoadingSpinnerComponent)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ChangeDetectorRef), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef)); };
IsLoadingSpinnerComponent.ɵcmp = /*@__PURE__*/ ɵngcc0.ɵɵdefineComponent({ type: IsLoadingSpinnerComponent, selectors: [["sw-is-loading-spinner"]], hostAttrs: [1, "sw-is-loading-spinner"], decls: 0, vars: 0, template: function IsLoadingSpinnerComponent_Template(rf, ctx) { }, encapsulation: 2 });
IsLoadingSpinnerComponent.ctorParameters = () => [
    { type: ChangeDetectorRef },
    { type: ElementRef }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IsLoadingSpinnerComponent, [{
        type: Component,
        args: [{
                selector: 'sw-is-loading-spinner',
                template: ``,
                host: { class: 'sw-is-loading-spinner' }
            }]
    }], function () { return [{ type: ɵngcc0.ChangeDetectorRef }, { type: ɵngcc0.ElementRef }]; }, null); })();

const SW_IS_LOADING_DIRECTIVE_CONFIG = new InjectionToken('SW_IS_LOADING_DIRECTIVE_CONFIG');

// This code was inspired by angular2-promise-buttons
// https://github.com/johannesjo/angular2-promise-buttons
class IsLoadingDirective {
    constructor(config, renderer, isLoadingService, el, componentFactoryResolver, injector) {
        this.renderer = renderer;
        this.isLoadingService = isLoadingService;
        this.el = el;
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this._isLoading = false;
        this.config = config || {};
        this._swIsLoadingDisableEl =
            this.config.disableEl === undefined ? true : this.config.disableEl;
        this._swIsLoadingSpinner =
            this.config.addSpinnerEl !== undefined
                ? this.config.addSpinnerEl
                : this.el.nativeElement instanceof HTMLButtonElement ||
                    this.el.nativeElement instanceof HTMLAnchorElement;
        this.loadingClass = this.config.loadingClass
            ? this.config.loadingClass
            : "sw-is-loading";
    }
    set swIsLoading(value) {
        if (typeof value === "string") {
            this.stringValue(value);
        }
        else {
            this.notStringValue();
        }
        if (value instanceof Subscription) {
            this.subscriptionValue(value);
        }
        else {
            this.notSubscriptionValue();
        }
        if (value instanceof Promise) {
            this.promiseValue(value);
        }
        else {
            this.notPromiseValue();
        }
        if (typeof value === "boolean") {
            this.booleanValue(value);
        }
        else {
            this.notBooleanValue();
        }
        if (value instanceof Observable) {
            this.observableValue();
        }
        else {
            this.notObservableValue();
        }
    }
    set swIsLoadingDisableEl(value) {
        this._swIsLoadingDisableEl = coerceBooleanValue(value);
    }
    get swIsLoadingDisableEl() {
        return this._swIsLoadingDisableEl;
    }
    // By default, if this directive is attached to an anchor or a button
    // element, add a `sw-is-loading-spinner` element to the dom (for styling)
    set swIsLoadingSpinner(value) {
        this._swIsLoadingSpinner = coerceBooleanValue(value);
    }
    get swIsLoadingSpinner() {
        return this._swIsLoadingSpinner;
    }
    get isLoading() {
        return this._isLoading;
    }
    set pending(value) {
        this.startLoading();
        value.finally(() => this.stopLoading());
    }
    ngOnChanges(changes) {
        if (changes.swIsLoadingSpinner &&
            !changes.swIsLoadingSpinner.isFirstChange() &&
            !changes.swIsLoadingSpinner.currentValue) {
            this.removeSpinnerEl();
        }
        if (changes.swIsLoadingDisableEl &&
            !changes.swIsLoadingDisableEl.isFirstChange() &&
            !changes.swIsLoadingDisableEl.currentValue) {
            this.renderer.removeAttribute(this.el.nativeElement, "disabled");
        }
    }
    ngAfterViewInit() {
        if (this.swIsLoadingSpinner) {
            this.addSpinnerEl();
        }
    }
    ngOnDestroy() {
        if (this.textValueSubscription) {
            this.textValueSubscription.unsubscribe();
        }
    }
    startLoading() {
        this.renderer.addClass(this.el.nativeElement, this.loadingClass);
        if (this.swIsLoadingDisableEl) {
            this.renderer.setAttribute(this.el.nativeElement, "disabled", "disabled");
        }
        this._isLoading = true;
    }
    stopLoading() {
        this.renderer.removeClass(this.el.nativeElement, this.loadingClass);
        if (this.swIsLoadingDisableEl) {
            this.renderer.removeAttribute(this.el.nativeElement, "disabled");
        }
        this._isLoading = false;
    }
    addSpinnerEl() {
        this.spinnerEl = this.componentFactoryResolver
            .resolveComponentFactory(IsLoadingSpinnerComponent)
            .create(this.injector);
        // need to use Renderer2#appendChild instead of
        // ViewContainerRef#createComponent because the injected
        // view container is not for the element this directive
        // is applied to
        this.renderer.appendChild(this.el.nativeElement, this.spinnerEl.instance.el.nativeElement);
    }
    removeSpinnerEl() {
        if (!this.spinnerEl)
            return;
        this.renderer.removeChild(this.el.nativeElement, this.spinnerEl.instance.el.nativeElement);
        this.spinnerEl.destroy();
        this.spinnerEl = undefined;
    }
    /**
     * Input value handlers
     */
    stringValue(value) {
        if (this.textValueSubscription) {
            this.textValueSubscription.unsubscribe();
        }
        this.textValueSubscription = this.isLoadingService
            .isLoading$({
            key: value || "default",
        })
            .pipe(debounceTime(10), distinctUntilChanged())
            .subscribe((loading) => {
            if (loading) {
                this.startLoading();
            }
            else if (this.isLoading) {
                this.stopLoading();
            }
        });
    }
    notStringValue() {
        if (this.textValueSubscription) {
            this.textValueSubscription.unsubscribe();
        }
    }
    observableValue() {
        throw new TypeError("swBtnPending must be an instance of Subscription. Instance of Observable given.");
    }
    notObservableValue() { }
    subscriptionValue(value) {
        this.pending = new Promise((resolve) => value.add(resolve));
    }
    notSubscriptionValue() { }
    promiseValue(value) {
        this.pending = value;
    }
    notPromiseValue() { }
    booleanValue(value) {
        if (value) {
            this.pending = new Promise((resolve) => {
                this.booleanValueResolveFn = resolve;
            });
        }
        else if (this.booleanValueResolveFn) {
            this.booleanValueResolveFn();
        }
        else {
            this.stopLoading();
        }
    }
    notBooleanValue() {
        if (this.booleanValueResolveFn) {
            this.booleanValueResolveFn();
        }
    }
}
IsLoadingDirective.ɵfac = function IsLoadingDirective_Factory(t) { return new (t || IsLoadingDirective)(ɵngcc0.ɵɵdirectiveInject(SW_IS_LOADING_DIRECTIVE_CONFIG, 8), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.Renderer2), ɵngcc0.ɵɵdirectiveInject(IsLoadingService), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ComponentFactoryResolver), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.Injector)); };
IsLoadingDirective.ɵdir = /*@__PURE__*/ ɵngcc0.ɵɵdefineDirective({ type: IsLoadingDirective, selectors: [["", "swIsLoading", ""]], inputs: { swIsLoading: "swIsLoading", swIsLoadingDisableEl: "swIsLoadingDisableEl", swIsLoadingSpinner: "swIsLoadingSpinner" }, exportAs: ["swIsLoading"], features: [ɵngcc0.ɵɵNgOnChangesFeature] });
IsLoadingDirective.ctorParameters = () => [
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [SW_IS_LOADING_DIRECTIVE_CONFIG,] }] },
    { type: Renderer2 },
    { type: IsLoadingService },
    { type: ElementRef },
    { type: ComponentFactoryResolver },
    { type: Injector }
];
IsLoadingDirective.propDecorators = {
    swIsLoading: [{ type: Input }],
    swIsLoadingDisableEl: [{ type: Input }],
    swIsLoadingSpinner: [{ type: Input }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IsLoadingDirective, [{
        type: Directive,
        args: [{
                selector: "[swIsLoading]",
                exportAs: "swIsLoading"
            }]
    }], function () { return [{ type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [SW_IS_LOADING_DIRECTIVE_CONFIG]
            }] }, { type: ɵngcc0.Renderer2 }, { type: IsLoadingService }, { type: ɵngcc0.ElementRef }, { type: ɵngcc0.ComponentFactoryResolver }, { type: ɵngcc0.Injector }]; }, { swIsLoading: [{
            type: Input
        }], swIsLoadingDisableEl: [{
            type: Input
        }], swIsLoadingSpinner: [{
            type: Input
        }] }); })();
function coerceBooleanValue(val) {
    if (typeof val === "boolean")
        return val;
    if (["", "true"].includes(val))
        return true;
    return false;
}

class IsLoadingDirectiveModule {
}
IsLoadingDirectiveModule.ɵfac = function IsLoadingDirectiveModule_Factory(t) { return new (t || IsLoadingDirectiveModule)(); };
IsLoadingDirectiveModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: IsLoadingDirectiveModule });
IsLoadingDirectiveModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IsLoadingDirectiveModule, [{
        type: NgModule,
        args: [{
                declarations: [IsLoadingDirective, IsLoadingSpinnerComponent],
                entryComponents: [IsLoadingSpinnerComponent],
                exports: [IsLoadingDirective]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(IsLoadingDirectiveModule, { declarations: [IsLoadingDirective, IsLoadingSpinnerComponent], exports: [IsLoadingDirective] }); })();

class IsLoadingPipe {
    constructor(isLoadingService) {
        this.isLoadingService = isLoadingService;
    }
    transform(key) {
        return this.isLoadingService
            .isLoading$({ key })
            .pipe(debounceTime(10), distinctUntilChanged());
    }
}
IsLoadingPipe.ɵfac = function IsLoadingPipe_Factory(t) { return new (t || IsLoadingPipe)(ɵngcc0.ɵɵdirectiveInject(IsLoadingService, 16)); };
IsLoadingPipe.ɵpipe = /*@__PURE__*/ ɵngcc0.ɵɵdefinePipe({ name: "swIsLoading", type: IsLoadingPipe, pure: true });
IsLoadingPipe.ctorParameters = () => [
    { type: IsLoadingService }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IsLoadingPipe, [{
        type: Pipe,
        args: [{
                name: "swIsLoading"
            }]
    }], function () { return [{ type: IsLoadingService }]; }, null); })();

class IsLoadingPipeModule {
}
IsLoadingPipeModule.ɵfac = function IsLoadingPipeModule_Factory(t) { return new (t || IsLoadingPipeModule)(); };
IsLoadingPipeModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: IsLoadingPipeModule });
IsLoadingPipeModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({});
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IsLoadingPipeModule, [{
        type: NgModule,
        args: [{
                declarations: [IsLoadingPipe],
                exports: [IsLoadingPipe]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(IsLoadingPipeModule, { declarations: [IsLoadingPipe], exports: [IsLoadingPipe] }); })();

class IsLoadingModule {
}
IsLoadingModule.ɵfac = function IsLoadingModule_Factory(t) { return new (t || IsLoadingModule)(); };
IsLoadingModule.ɵmod = /*@__PURE__*/ ɵngcc0.ɵɵdefineNgModule({ type: IsLoadingModule });
IsLoadingModule.ɵinj = /*@__PURE__*/ ɵngcc0.ɵɵdefineInjector({ providers: [], imports: [[IsLoadingDirectiveModule, IsLoadingPipeModule], IsLoadingDirectiveModule, IsLoadingPipeModule] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IsLoadingModule, [{
        type: NgModule,
        args: [{
                imports: [IsLoadingDirectiveModule, IsLoadingPipeModule],
                providers: [],
                exports: [IsLoadingDirectiveModule, IsLoadingPipeModule]
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(IsLoadingModule, { imports: [IsLoadingDirectiveModule, IsLoadingPipeModule], exports: [IsLoadingDirectiveModule, IsLoadingPipeModule] }); })();

/*
 * Public API Surface of is-loading
 *
 * Important!
 * The files must be exported
 * individually without using an `index.ts` file (i.e.
 * `./lib/module/directive`). Otherwise there's some sort of
 * bug with `ng-packagr` or `angular` which prevents the
 * `service-work-is-loading.metadata.json` from being
 * properly built.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { IsLoadingDirective, IsLoadingDirectiveModule, IsLoadingModule, IsLoadingPipe, IsLoadingPipeModule, IsLoadingService, SW_IS_LOADING_DIRECTIVE_CONFIG, IsLoadingSpinnerComponent as ɵa };

